import React, { useContext, useEffect, useState } from "react"
import { errorToString, PrequalResult, dollars } from "@oneethos/shared"
import { FormInput, SubformProps, Validation } from "."
import { AddressInputs } from "../../components/forms"
import api from '../../api-client'
import { PrequalHelperVariant, useAppState, usePrequalPasteHelper } from "../../hooks"
import { AppContext } from "../../appContext"
import { requestLoginViaEmail, requestRegistrationViaEmail } from "../../components"
import { Spinner } from "@fluentui/react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { formDataToLoanApp, lappToFormData } from "./util"
import { CoborrowerPrequal } from "./coborrower-prequal"
import { PrequalLetter } from "./prequal-letter"
import { ConsumerErrorView } from "./consumer-error-view"
import { AddOrDeclineCoborrower } from "../../components/add-or-decline-coborrower"
import SelectLoanTerm from "./select-loan-term"
import { PrimaryBorrowerPrequal } from "./primary-borrower-prequal"
import { toast } from "react-toastify"

export const QualificationCriteria = () => <ul>
  <li>Credit score must be 680 or higher</li>
  <li>Debt to income ratio must be 50% or less</li>
  <li>Discuss alternative financing options with your solar installer</li>
  <li>Consider including a Co-Borrower in your application</li>
</ul>


const PreQualSuccess = ({ formData, update, save }: Partial<SubformProps>) => {
  const { tenant } = useAppState()

  return <div className="prequal-success pb-4">
    <div className="alert alert-success">
      <b>Great News!</b> Based on the information you have provided, you are prequalified for
      the following Clean Energy loan options.
    </div>
    <h4 className="mt-2">Next: Complete Your Clean Energy Loan Application</h4>
    <p>
      Now that you're prequalified, we'll collect the information
      necessary to proceed with your loan application.
    </p>

    <SelectLoanTerm
      formData={formData}
      update={update}
      save={save}
    />

    {formData?.productId ? <div className="my-4">
      <h4>Review Prequalification Letter</h4>
      <PrequalLetter
        formData={formData}
        update={update}
        save={save}
      />
    </div> : null}

    {formData?.productId && formData.preapprovalLetter?.agreement?.agrees ? <>
      <div className="buttons d-flex justify-content-start mt-4">
        <button type="button"
          className="btn btn-primary"
          onClick={() => save()}>Continue</button>
      </div>
    </> : <div className="alert alert-secondary">
      Please review the prequalification letter and select a loan option to continue.
    </div>}

  </div>
}

export const PreQualFailure = ({ result }: { result?: PrequalResult }) => {
  const { tenant } = useAppState()

  if (result === 'no-hit') {
    return <div className="prequal-no-hit">
      <p>
        We couldn't find any information to determine whether you prequalify for a Clean Energy
        loan with {tenant.config.name}. Please consider the following before applying.
      </p>
      <QualificationCriteria />
    </div>
  }

  return <div className="prequal-failure">
    <div>
      You might not qualify for a Clean Energy loan with {tenant.config.name}.
      Consider the following before applying.
    </div>
    <QualificationCriteria />
  </div>
}

export const EligibilityFields = ({ formData, update }: Partial<SubformProps>) => {
  usePrequalPasteHelper(update, PrequalHelperVariant.PrimaryEligibility)

  return <>
    <div className="row">
      <div className="col-md-4">
        <FormInput
          field='firstName'
          value={formData.firstName}
          onChange={s => update('firstName', s)}
        />
      </div>
      <div className="col-md-2">
        <FormInput
          field='middleName'
          value={formData.middleName}
          emptyOk
          onChange={s => update('middleName', s)}
        />
      </div>
      <div className="col-md-6">
        <FormInput
          field='lastName'
          value={formData.lastName}
          onChange={s => update('lastName', s)}
        />
      </div>
    </div>
    <FormInput
      field='email'
      value={formData.email || ''}
      onChange={s => update('email', s)}
      disabled
    />
    <FormInput
      field='phone'
      value={formData.phone || ''}
      onChange={s => update('phone', s)}
    />
    <div className="form-group">
      <label>Do you live at a different address than where the panels will be installed?</label>
      <select
        className="form-select"
        value={formData.installedAtDifferentLocation}
        onChange={ev => {
          const val = ev.target.value as 'yes' | 'no'

          // initialize the state of installationAddress, or clear it
          update({
            ...formData,
            userAddress: val === 'yes' ? {
              state: 'FL',
              country: 'United States'
            } : formData.installationAddress,
            installedAtDifferentLocation: val
          })
        }}
      >
        <option value='no'>No</option>
        <option value='yes'>Yes</option>
      </select>
    </div>
    {formData.installedAtDifferentLocation === 'yes' ? (
      <AddressInputs
        address={formData.userAddress}
        label="Home Address"
        onChange={address => update('userAddress', address)}
      />
    ) : null}
    <div className="row">
      <div className="col-md-6">
        <FormInput
          field='statedGrossAnnualIncome'
          value={formData.statedGrossAnnualIncome}
          onChange={s => update('statedGrossAnnualIncome', s)}
        />
      </div>
      <div className="col-md-6">
        <FormInput
          field='birthDate'
          value={formData.birthDate || ''}
          onChange={s => update('birthDate', s)}
        />
      </div>
    </div>
  </>
}

const RegistrationRequired = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const query = new URLSearchParams({ route: `/apply/${id}` })
  const url = `/login?${query.toString()}`

  return <div>
    <p>
      To protect your security, we have sent you an email to verify your information. Please
      check your email to continue the application process.
    </p>
    <div className="buttons text-start mb-3">
      <button
        type="button"
        className="btn btn-primary"
        onClick={() => {
          const query = new URLSearchParams({ route: `/apply/${id}` })
          navigate(`/verify-email?${query.toString()}`)
        }}
      >Enter Code</button>
    </div>
    <p>
      Didn't receive the email or having issues? You may
      try to <Link to={url}>login or register</Link> to
      resume your application.
    </p>
  </div>
}

type SubmittingStatus = 'identity' | 'prequal' | 'combined' | 'prequal-done' | 'combined-done' | 'error' | ''

export const EligibilityContent = ({ formData, schema, update, save, onError }: SubformProps) => {
  const { state: { registration: { installer } } } = useContext(AppContext)
  const [submitting, setSubmitting] = useState<SubmittingStatus>('')
  const [error, setError] = useState<string | null>(null)
  const [registrationRequired, setRegistrationRequired] = useState(false)
  const agreesToTerms = formData.agreesToTermsAndConditions?.agrees

  const handleError = (ex: any) => {
    if (ex.error?.includes('validation failed')) {
      // validation errors should prevent moving forward
      setSubmitting('')
      return toast.error(ex.error)
    }

    if (ex.code === 'ALREADY_REGISTERED') {
      return requestLoginViaEmail({
        email: formData.email,
        route: `/apply/${formData.lookup}`
      }).then(() => setRegistrationRequired(true))
        .catch(ex => onError(errorToString(ex)))
    } else if (ex.code !== 'UNREGISTERED') {
      // most users have probably not registered because they're new referrals
      setError(ex)
      setSubmitting('error')
    }
  }

  const initiateRegistration = () => {
    setSubmitting('identity')

    return requestRegistrationViaEmail({
      email: formData.email,
      phone: formData.phone,
      name: `${formData.firstName} ${formData.lastName}`,
      route: `/apply/${formData.lookup}`
    }).then(() => setRegistrationRequired(true))
      .catch(handleError)
      .finally(() => setSubmitting(''))
  }

  const submitPrequal = async (testPrequalResult?: PrequalResult) => {
    setSubmitting(installer ? 'prequal' : 'identity')
    update({ prequalError: null })

    try {
      // patch directly instead of using form-level save() because validation errors should prevent moving forward;
      // form-level save() has internal error handling that won't trigger the catch block below
      const patch = formDataToLoanApp({ ...formData, testPrequalResult })
      await api.patch(`/c/loanapps/${formData._id}`, patch)

      if (!installer) {
        await initiateRegistration()
      } else {
        const res = await api.post(`/c/loanapps/${formData._id}/prequal`)

        update(lappToFormData(res))

        if (['no-hit', 'reject'].includes(res.prequalResult)) {
          setSubmitting('')
        } else {
          setSubmitting('prequal-done')
        }
      }
    } catch (ex) {
      handleError(ex)
    }
  }

  const submitCombinedPrequal = async (testPrequalResultCombined?: 'pass' | 'fail' | 'no-hit') => {
    setSubmitting('combined')

    try {
      // see notes in submitPrequal() above
      const patch = formDataToLoanApp({ ...formData, testPrequalResultCombined })
      await api.patch(`/c/loanapps/${formData._id}`, patch)

      const res = await api.post(`/c/loanapps/${formData._id}/prequal-combined`)
      update(lappToFormData(res))
      setSubmitting('combined-done')
    } catch (ex) {
      handleError(ex)
    }
  }

  useEffect(() => {
    if (!installer && formData.prescreened && agreesToTerms) {
      initiateRegistration()
    }
  }, [])

  if (registrationRequired) {
    return <RegistrationRequired />
  }

  if (submitting === 'prequal') {
    return <div>
      <p>Checking your eligibility...</p>
      <Spinner />
    </div>
  } else if (submitting === 'identity') {
    return <div>
      <p>Verifying security requirements...</p>
      <Spinner />
    </div>
  } else if (submitting === 'combined') {
    return <div>
      <p>Checking combined eligibility (with coborrower)...</p>
      <Spinner />
    </div>
  } else if (submitting === 'error') {
    return <div className="alert alert-danger">
      <p>An error occurred. You may attempt to refresh the page or try again later.</p>
      <p>Error details: {JSON.stringify(error)}</p>
    </div>
  }

  if (formData.prequalResult === 'pass' || formData.prequalResultCombined === 'pass') {
    return <PreQualSuccess formData={formData} update={update} save={save} />
  } else if (
    ['fail', 'no-hit-final', 'reject-final'].includes(formData.prequalResult) &&
    formData.includeCoBorrower !== 'yes'
  ) {
    return <div>
      <PreQualFailure result={formData.prequalResult} />
      <AddOrDeclineCoborrower
        formData={formData}
        update={update}
        save={save}
      />
    </div>
  } else if (formData.includeCoBorrower === 'yes' && (
    !formData.prequalResultCombined ||
    ['no-hit', 'error', 'reject', 'pending'].includes(formData.prequalResultCombined)
  )) {
    return <CoborrowerPrequal
      formData={formData}
      schema={schema}
      update={update}
      submitCombinedPrequal={submitCombinedPrequal}
      onError={onError}
    />
  }

  return <PrimaryBorrowerPrequal
    formData={formData}
    schema={schema}
    update={update}
    submitPrequal={submitPrequal}
    installer={installer}
  />
}

const EligibilityError = ({ formData }: SubformProps) => {
  return formData.prequalError ? <div className="prequal-error alert alert-danger">
    <p><b>An error occurred while attempting to check your eligibility.</b> You may attempt to resolve the issue or try again later.</p>
    <div>
      {typeof formData.prequalError.data?.additionalErrorDetails === "string" ?
        <div
          dangerouslySetInnerHTML={{ __html: formData.prequalError.data?.additionalErrorDetails }}
          style={{ fontSize: "0.8em" }}
        /> :
        <>
          <div className="fw-bold">Error Details:</div>
          <div>{formData.prequalError.error}</div>
        </>
      }
    </div>
  </div> : null
}

export const Eligibility = (props: SubformProps) => {
  return <div>
    <EligibilityError {...props} />
    <EligibilityContent {...props} />
  </div>
}

export default Eligibility